import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  InventoryAttachment,
  InventoryItem,
} from '../../models/inventory/inventory-item';
import { formatInventoryFileSize } from '../../models/inventory/inventory-limits';
import { createInventoryTranslationRequest } from '../../models/inventory/inventory-translation-request';

export type ItemTab = 'details' | 'attachments';

@Component({
  selector: 'app-inventory-item-detail',
  template: `
    <div class="tw-w-full app-pl4 app-pb3">
      <div class="inventory-item-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          class="inventory-item-tab"
          [class.active]="tab === 'details'"
          [attr.aria-selected]="tab === 'details'"
          (click)="selectTab('details')"
        >
          {{ 'Details' | translation: translationRequest }}
        </button>
        <button
          type="button"
          role="tab"
          class="inventory-item-tab"
          [id]="'inventory-attachments-tab-' + item.id"
          [class.active]="tab === 'attachments'"
          [attr.aria-selected]="tab === 'attachments'"
          (click)="selectTab('attachments')"
        >
          {{ 'Attachments' | translation: translationRequest }}
        </button>
      </div>

      @if (tab === 'details') {
        <div class="tw-flex tw-flex-col tw-w-full app-pt2 tw-gap-y-2">
          <div class="tw-flex tw-flex-wrap tw-w-full">
            <div class="tw-w-full md:tw-w-[50%] md:tw-pr-4">
              <app-value
                label="Item description"
                [value]="item.itemDescription"
                layout="col"
                [translationNeeded]="false"
              ></app-value>
            </div>
            <div class="tw-w-full md:tw-w-[50%]">
              <app-value
                label="Date of purchase"
                [value]="item.dateOfPurchase | format: 'date'"
                layout="col"
                [translationNeeded]="false"
              ></app-value>
            </div>
          </div>
          <div class="tw-flex tw-flex-wrap tw-w-full">
            <div class="tw-w-full md:tw-w-[50%] md:tw-pr-4">
              <app-value
                label="Additional item details"
                [value]="item.additionalItemDetails"
                layout="col"
                [translationNeeded]="false"
              ></app-value>
            </div>
            <div class="tw-w-full md:tw-w-[50%]">
              <app-value
                label="Method of payment"
                [value]="methodOfPaymentDisplay"
                layout="col"
                [translationNeeded]="false"
              ></app-value>
            </div>
          </div>
          <div class="tw-flex tw-flex-wrap tw-w-full">
            <div class="tw-w-full md:tw-w-[50%] md:tw-pr-4">
              <app-value
                label="Pre-loss condition"
                [value]="item.preLossCondition"
                layout="col"
                [translationNeeded]="false"
              ></app-value>
            </div>
            <div class="tw-w-full md:tw-w-[50%]">
              <app-value
                label="Receipt or invoice included"
                [value]="item.receiptOrInvoiceIncluded"
                layout="col"
                [translationNeeded]="false"
              ></app-value>
            </div>
          </div>
          <div class="tw-flex tw-flex-wrap tw-w-full">
            <div class="tw-w-full md:tw-w-[50%] md:tw-pr-4">
              <app-value
                label="Where purchased"
                [value]="item.wherePurchased"
                layout="col"
                [translationNeeded]="false"
              ></app-value>
            </div>
            @if (item.receiptOrInvoiceIncluded === 'No') {
              <div class="tw-w-full md:tw-w-[50%]">
                <app-value
                  label="Reason for no supporting documentation"
                  [value]="item.reasonForNoDocumentation"
                  layout="col"
                  [translationNeeded]="false"
                  [translationLabelNeeded]="false"
                ></app-value>
              </div>
            }
          </div>
        </div>
      } @else {
        <div
          class="inventory-attachments-panel"
          [id]="'inventory-attachments-panel-' + item.id"
          tabindex="-1"
          [attr.aria-label]="'Attachments' | translation: translationRequest"
        >
          @if (item.attachments?.length > 0) {
            <app-list
              [dataSource]="item.attachments"
              [template]="attachmentRow"
              [filterEnabled]="false"
              [paginatorEnabled]="true"
              [includeNoDataIcon]="false"
              [loaded]="true"
              [tableAriaLabel]="'Item attachments'"
              DefaultSortColumn="dateUploaded"
              DefaultSortDir="desc"
              [staticSortLabel]="true"
            >
              <ng-container header>
                <tr class="tw-flex tw-flex-wrap tw-w-full">
                  <app-list-header
                    name="fileName"
                    label="Document"
                    class="tw-w-full md:tw-w-[34%]"
                  ></app-list-header>
                  <app-list-header
                    name="dateUploaded"
                    label="Date uploaded"
                    class="tw-w-full md:tw-w-[28%]"
                  ></app-list-header>
                  <app-list-header
                    name="fileSize"
                    label="File size"
                    class="tw-w-full md:tw-w-[18%]"
                  ></app-list-header>
                  <app-list-header
                    label="Remove"
                    readOnly
                    class="tw-w-full md:tw-w-[20%]"
                  ></app-list-header>
                </tr>
              </ng-container>
            </app-list>

            <ng-template #attachmentRow let-attachment>
              <tr
                class="tw-flex tw-flex-wrap tw-w-full tw-items-center app-pt1 app-pb1"
              >
                <td class="tw-w-full md:tw-w-[34%]">
                  <app-value
                    label="Document"
                    [value]="attachment.fileName"
                    layout="row-xs"
                    [translationNeeded]="false"
                  ></app-value>
                </td>
                <td class="tw-w-full md:tw-w-[28%]">
                  <app-value
                    label="Date uploaded"
                    [value]="
                      attachment.contentPurged
                        ? 'Removed after claim closure'
                        : (attachment.dateUploaded | format: 'dateTime')
                    "
                    layout="row-xs"
                    [translationNeeded]="false"
                  ></app-value>
                </td>
                <td class="tw-w-full md:tw-w-[18%]">
                  <app-value
                    label="File size"
                    [value]="fileSizeDisplay(attachment)"
                    layout="row-xs"
                    [translationNeeded]="false"
                  ></app-value>
                </td>
                <td class="tw-w-full md:tw-w-[20%]">
                  <app-icon-button
                    icon="delete_outline"
                    label="Remove attachment"
                    tabindex="0"
                    role="button"
                    ariaLabelText="Remove attachment"
                    [disabled]="readOnly"
                    (click)="removeAttachment.emit(attachment)"
                    (keydown.enter)="removeAttachment.emit(attachment)"
                  ></app-icon-button>
                </td>
              </tr>
            </ng-template>
          } @else {
            <div class="app-pt2 app-pb2">
              {{
                'No attachments added for this item.'
                  | translation: translationRequest
              }}
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      .inventory-item-tabs {
        display: flex;
        border-bottom: 1px solid #dedede;
      }

      .inventory-item-tab {
        background: none;
        border: none;
        height: 50px;
        padding: 0 12px;
        font-weight: 700;
        font-size: 16px;
        letter-spacing: normal;
        text-transform: uppercase;
        color: var(--sed-black-80-color);
        cursor: pointer;
        border-bottom: 4px solid transparent;
        margin-bottom: -1px;
      }

      .inventory-item-tab.active {
        color: var(--sed-engaged-black-color);
        font-weight: 750;
        border-bottom-color: var(--sed-sedgwick-green-color);
      }

      .inventory-item-tab:hover {
        background-color: var(--sed-open-grey-color);
      }

      .inventory-attachments-panel:focus,
      .inventory-attachments-panel app-icon-button:focus {
        outline: 2px solid var(--sed-dynamic-black-color);
        outline-offset: 2px;
      }
    `,
  ],
  standalone: false,
})
export class InventoryItemDetailComponent {
  @Input()
  item: InventoryItem;

  @Input()
  readOnly = false;

  @Input()
  set activeTab(value: ItemTab) {
    this.tab = value ?? 'details';
  }

  @Output()
  removeAttachment = new EventEmitter<InventoryAttachment>();

  @Output()
  tabChange = new EventEmitter<ItemTab>();

  tab: ItemTab = 'details';

  translationRequest = createInventoryTranslationRequest();

  selectTab(tab: ItemTab): void {
    this.tab = tab;
    this.tabChange.emit(tab);
  }

  fileSizeDisplay(attachment: InventoryAttachment): string {
    return formatInventoryFileSize(attachment.fileSize ?? attachment.file?.size);
  }

  get methodOfPaymentDisplay(): string {
    return this.item.methodOfPayment === 'Other' &&
      this.item.specifyMethodOfPayment
      ? this.item.specifyMethodOfPayment
      : this.item.methodOfPayment;
  }
}
